import { memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useMeasure } from "@uidotdev/usehooks";
import {
  FILE_PREVIEW_DEFAULT_MAX_WIDTH,
  FILE_PREVIEW_DEFAULT_MAX_HEIGHT,
} from "@/constants";

interface FilePreviewPdfIframePreviewProps {
  src: string;
  isFullScreen?: boolean;
  maxWidth?: number;
  maxHeight?: number;
}

export const FilePreviewPdfIframePreview = memo(
  ({
    src,
    isFullScreen,
    maxWidth = FILE_PREVIEW_DEFAULT_MAX_WIDTH,
    maxHeight = FILE_PREVIEW_DEFAULT_MAX_HEIGHT,
  }: FilePreviewPdfIframePreviewProps) => {
    const { t } = useTranslation();
    const [containerRef, { width: containerWidth }] =
      useMeasure<HTMLDivElement>();

    const iframeStyle = useMemo(() => {
      if (isFullScreen) return undefined;
      const width = containerWidth
        ? Math.min(maxWidth, containerWidth)
        : maxWidth;
      return {
        width: `${width}px`,
        height: `${maxHeight}px`,
        maxWidth: "100%",
      };
    }, [isFullScreen, maxWidth, maxHeight, containerWidth]);

    if (isFullScreen) {
      return (
        <div className="flex flex-col h-full">
          <div className="flex-1 bg-gray-100 rounded-lg overflow-auto">
            <iframe
              src={src}
              title={t("filePreview.previewFile")}
              className="border-none w-full h-full min-w-fit"
            />
          </div>
        </div>
      );
    }

    return (
      <div ref={containerRef} className="w-full flex flex-col items-center">
        <div className="bg-gray-100 rounded-lg overflow-auto mx-auto max-w-full w-full flex justify-center">
          <iframe
            src={src}
            title={t("filePreview.previewFile")}
            className="border-none max-w-full"
            style={iframeStyle}
          />
        </div>
      </div>
    );
  },
);
